import { Box, IconButton, Chip } from "@mui/material";
import { Check, ChevronLeft, ChevronRight } from "@mui/icons-material";
import CustomTypography from "../../../Components/Typography";
import {
  headerBoxStyle,
  avgDiscountChipStyle,
  stepperItemStyle,
  stepperBadgeStyle,
} from "../styles/styles";
import { useTranslation } from "react-i18next";
import CustomTooltipBox from "../../../Components/CustomTooltips/CustomTooltipBox";

const SupplierProfileHeader = (props) => {
  const {
    viewType,
    supplierProfileData,
    selectedPricelists,
    onClose,
    setOpenDiscardChangesModal,
    isSmallScreen,
    showServicesStep = false,
    showProductsStep = false,
    showRestaurantsStep = false,
    activeStep,
    averageDiscount,
    productsSelectedCount = 0,
    productsTotalCount = 0,
    restaurantsSelectedCount = 0,
    restaurantsTotalCount = 0,
  } = props;

  // Step counts (selected / total), matching each step's footer counter.
  const servicesSelectedCount = selectedPricelists?.length ?? 0;
  const servicesTotalCount =
    supplierProfileData?.pricelists?.filter((p) => p.is_offered).length ?? 0;

  const { t, i18n } = useTranslation([
    "supplier-profile-header",
    "supplier-products-info",
    "tooltip-sections",
  ]);
  const { supplier, pricelists } = supplierProfileData;

  // The stepper renders whichever category steps this supplier actually has,
  // in a fixed order. Services + one catalog gives the original two-step flow;
  // a supplier with both a store and a menu gets three.
  const stepperSteps = [
    showServicesStep && {
      key: "services",
      label: t("steps.services", { ns: "supplier-products-info" }),
      selectedCount: servicesSelectedCount,
      totalCount: servicesTotalCount,
    },
    showProductsStep && {
      key: "products",
      label: t("steps.products", { ns: "supplier-products-info" }),
      selectedCount: productsSelectedCount,
      totalCount: productsTotalCount,
    },
    showRestaurantsStep && {
      key: "restaurants",
      label: t("steps.restaurants", { ns: "supplier-products-info" }),
      selectedCount: restaurantsSelectedCount,
      totalCount: restaurantsTotalCount,
    },
  ].filter(Boolean);

  const isHebrew = i18n.language === "he";
  const arrowIcon = isHebrew ? "/right_arrow_icon.svg" : "/left_arrow_icon.svg";
  const StepChevron = isHebrew ? ChevronLeft : ChevronRight;

  const handleCancel = () => {
    if (
      viewType === "view" &&
      selectedPricelists.length !==
        pricelists.filter((p) => p.is_offered && p.is_accepted).length
    ) {
      setOpenDiscardChangesModal(true);
    } else {
      onClose();
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={headerBoxStyle}
    >
      <Box display="flex" alignItems="center" gap="12px">
        <Box
          onClick={handleCancel}
          sx={{ cursor: "pointer", display: { xs: "flex", sm: "none" } }}
        >
          <img src={arrowIcon} alt="left_arrow_icon" />
        </Box>

        <CustomTypography
          type="subheading"
          props={{ color: "#2F2B3D", lineHeight: "38px" }}
        >
          {isSmallScreen
            ? viewType === "viewAsCommunityManager"
              ? t("viewing_as_community_manager")
              : supplier.name
            : t("supplier_info")}
          {!isSmallScreen &&
            viewType === "viewAsCommunityManager" &&
            ` ${t("view_as_community_manager")}`}
        </CustomTypography>

        {averageDiscount != null && (
          <Chip
            sx={{ ...avgDiscountChipStyle, display: { xs: "none", md: "flex" } }}
            label={`${t("average_discount", {
              ns: "supplier-products-info",
            })}: ${Math.round(averageDiscount)}%`}
          />
        )}
      </Box>

      {stepperSteps.length > 1 && (
        <Box
          display="flex"
          alignItems="center"
          gap="12px"
          sx={{ display: { xs: "none", md: "flex" } }}
        >
          {stepperSteps.map((step, index) => {
            // A step is "done" once the CM has moved past it, which is what the
            // check badge means; the badge is otherwise its 1-based position.
            const activeIndex = stepperSteps.findIndex(
              (s) => s.key === activeStep
            );
            const isDone = activeIndex > index;
            return (
              <Box key={step.key} display="flex" alignItems="center" gap="12px">
                {index > 0 && (
                  <StepChevron sx={{ fontSize: "18px", color: "#2F2B3D66" }} />
                )}
                <Box sx={stepperItemStyle(activeStep === step.key)}>
                  <Box component="span" sx={stepperBadgeStyle()}>
                    {isDone ? (
                      <Check sx={{ fontSize: "14px", color: "#257C6D" }} />
                    ) : (
                      index + 1
                    )}
                  </Box>
                  {step.label} ({step.selectedCount}/{step.totalCount})
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <Box
        display="flex"
        alignItems="center"
        gap="2px"
        sx={{ display: { xs: "none", sm: "flex" } }}
      >
        {viewType !== "viewAsCommunityManager" && (
          <CustomTooltipBox
            sections={t("supplier_profile_modal", {
              returnObjects: true,
              ns: "tooltip-sections",
            })}
          />
        )}
        <IconButton onClick={handleCancel}>
          <img src="/cancel_icon.svg" alt="close" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default SupplierProfileHeader;
